import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import moment from 'moment'
import { IconContext } from 'react-icons'
import { IoMdCloseCircle } from 'react-icons/io'
import { Alert } from '@wellbeingxltd/fe-lib/components'
import { GET_STORE } from '@wellbeingxltd/fe-lib/constants'
import AnimatedLogo from '@wellbeingxltd/fe-lib/images/dist/logo/AnimatedLogo.gif'
import { Button, Table } from 'components'
import { AnalyticsActions } from 'store/models/analytics'
import TicketsActions from 'store/models/tickets'
import TransactionsActions from 'store/models/transactions'
import EmailModal from './EmailModal'
import Thread from './Thread'
import { columnsData } from './columnsData'
import './styles.scss'

const Tickets = () => {
    const { allTickets, tickets, loadingTickets, loadingCloseTicket } = useSelector(GET_STORE.TICKETS)
    const { transactions, loadingAskForHelp, askForHelpError } = useSelector(GET_STORE.TRANSACTIONS)
    const dispatch = useDispatch()
    const history = useHistory()
    const params = useParams()
    const { id } = params

    const [currentTicket, setCurrentTicket] = useState(null)
    const [allTransTickets, setAllTransTickets] = useState([])
    const [showEmailModal, setShowEmailModal] = useState(false)
    const [emailTransaction, setEmailTransaction] = useState(null)
    const [emailMsg, changeEmailMsg] = useState('')
    const [error, setError] = useState('')

    useEffect(() => {
        document.title = 'Tickets | Betterspace Supplier'
        dispatch(AnalyticsActions.postAnalytics('[Tickets] componentDidMount', history.location.pathname))
    }, [])

    useEffect(() => {
        dispatch(TicketsActions.getTickets())
    }, [id])

    useEffect(() => {
        if (tickets.length) {
            setCurrentTicket(tickets.find(({ transactionId, ticketStatus }) => transactionId === id && ticketStatus !== 'Ticket Closed'))
            setAllTransTickets(allTickets.filter(({ transactionId }) => transactionId === id))
        }
    }, [tickets, id, dispatch])

    const onEmailClicked = () => {
        setEmailTransaction(transactions.find(({ transactionId }) => transactionId === id))
        setShowEmailModal(true)
    }

    const askForHelpCallback = (message, type) => {
        setShowEmailModal(false)
        changeEmailMsg('')
        if (type === 'success') {
            dispatch(TicketsActions.getTickets())
            setError('')
        } else {
            setError(
                `Sorry, something went wrong with sending the email. Please send a screenshot of this page to support@betterspace.uk and we'll look into it. Error: ${askForHelpError}`
            )
        }
    }

    return loadingTickets ? (
        <div className="centerLoader">
            <img src={AnimatedLogo} width="128px" alt="Full screen loading spinner" />
        </div>
    ) : (
        <div className="tickets-page">
            <div className="tickets-page-header">
                <h2>Tickets for transaction:</h2>
                <p>{id}</p>
            </div>
            <p className="tickets-page-current-status">Support ticket status:</p>
            <div className="tickets-page-current">
                <p>
                    {currentTicket
                        ? `Opened: ${moment(currentTicket.ticketOpenDate).format('Do MMM YYYY')} by ${
                              currentTicket.ticketType === 'supplier-to-user' ? 'Supplier' : 'User'
                          }`
                        : 'No opened tickets'}
                </p>
                <Button
                    size="small"
                    className="supplierTransactionsPill supplierTransactionsPill--blue supplierTransactionsAction"
                    onClick={() =>
                        currentTicket
                            ? dispatch(
                                  TicketsActions.closeTicket(currentTicket.ticketId, (message, type) => {
                                      Alert(message, type)
                                      if (type === 'success') dispatch(TicketsActions.getTickets())
                                  })
                              )
                            : onEmailClicked()
                    }
                    loading={loadingCloseTicket}
                >
                    {currentTicket ? 'Close ticket' : 'Start a new email conversation with user'}
                </Button>
                <EmailModal
                    showEmailModal={showEmailModal}
                    emailTransaction={emailTransaction}
                    changeEmailMsg={changeEmailMsg}
                    emailMsg={emailMsg}
                    setShowEmailModal={setShowEmailModal}
                    onAskForHelp={answers => dispatch(TransactionsActions.askForHelp(answers, emailTransaction, askForHelpCallback))}
                    loadingAskForHelp={loadingAskForHelp}
                />
            </div>
            {error && (
                <p className="error" role="alert">
                    <IconContext.Provider value={{ className: 'errorIcon' }}>
                        <IoMdCloseCircle />
                    </IconContext.Provider>
                    {error}
                </p>
            )}
            <Table
                title="Ticket archive"
                subtitleAsHtml={`<p>The message you send to the user through this dashboard, as well as tickets opened by the user, will appear in the inbox of the primary email address for your account (contact/main login email). They will also appear in the inbox of each manager that has permission to receive CS emails.</p><p>To continue the conversation, please respond to the email in your inbox as you would normally and all responses will to go all managers, Betterspace CS team and the User. All responses on the email thread are also stored on the open ticket on the dashboard. It is not yet possible to reply via the dashboard.</p>`}
                data={allTransTickets}
                columns={columnsData}
                emptyStateText="No past tickets were found"
                expandOnRowClick={true}
                renderExpandedView={ticket => <Thread ticket={ticket} />}
                sortable={true}
                initialSortBy={[{ id: 'ticketOpenDate', desc: true }]}
                tableClassName="tableClassName"
            />
        </div>
    )
}

export default Tickets
